import { col, type Shape } from './base.ts';

export const FIELD_SYSTEM_OPT_EXTRAS: Shape = [
  col('gasLiftRate', 'gasRate', 'System-optimized gas-lift injection rate.', { suffix: 'GasLiftRate' }),
  col('power', 'power', 'System-optimized electrical power draw.', { suffix: 'Power' }),
];

/**
 * The four calculated total production rates (oil/gas/water/liquid) shared by the
 * field- and reservoir-grain network-output sources. Bare canonical source
 * columns — `suffix === canonical`, `prefix:''`.
 */
export const CALC_TOTAL_RATES: Shape = [
  col('calculatedTotalOilRate', 'liquidRate', 'Calculated total oil rate.'),
  col('calculatedTotalGasRate', 'gasRate', 'Calculated total gas rate.'),
  col('calculatedTotalWaterRate', 'liquidRate', 'Calculated total water rate.'),
  col('calculatedTotalLiquidRate', 'liquidRate', 'Calculated total liquid rate.'),
];

/**
 * Field-grain calculated joint / separator / total rates and pressures on
 * `NetworkOutputs_Field_Agent`. Bare canonical source columns —
 * `suffix === canonical`, `prefix:''` — so this is a pure passthrough
 * projection. Reuses the shared {@link CALC_TOTAL_RATES} for the four production
 * totals, then adds the injection totals, joint/separator rates and flowline
 * outputs. Preserves the two VERBATIM source typos `calcualtedSeparatorGasRate`
 * ("calcualted") and `calculatedSepratorWaterRate` ("Seprator"); every suffix
 * must resolve (a typo would silently degrade to `unknownCol`).
 */
export const FIELD_CALC_TOTALS: Shape = [
  ...CALC_TOTAL_RATES,
  col('calculatedTotalGasRateGI', 'gasRate', 'Calculated total gas-injection rate.'),
  col('calculatedTotalWaterRateWI', 'liquidRate', 'Calculated total water-injection rate.'),
  col('calculatedTotalLiquidRateWI', 'liquidRate', 'Calculated total liquid-injection rate.'),
  col('calculatedJointOilRate', 'liquidRate', 'Calculated commingling-joint oil rate.'),
  col('calculatedJointGasRate', 'gasRate', 'Calculated commingling-joint gas rate.'),
  col('calculatedJointLiquidRate', 'liquidRate', 'Calculated commingling-joint liquid rate.'),
  col('calculatedJointPressure', 'pressureAbsolute', 'Calculated commingling-joint pressure.'),
  col('calculatedJointCo2', 'unitless', 'Calculated commingling-joint CO2 fraction.'),
  col('calculatedJointN2', 'unitless', 'Calculated commingling-joint N2 fraction.'),
  col('calculatedJointH2S', 'unitless', 'Calculated commingling-joint H2S fraction.'),
  col('calculatedSeparatorOilRate', 'liquidRate', 'Calculated separator oil rate.'),
  // VERBATIM source typo ("calcualted") — must be preserved to resolve.
  col('calculatedSeparatorGasRate', 'gasRate', 'Calculated separator gas rate.', {
    suffix: 'calcualtedSeparatorGasRate',
  }),
  // VERBATIM source typo ("Seprator") — must be preserved to resolve.
  col('calculatedSeparatorWaterRate', 'liquidRate', 'Calculated separator water rate.', {
    suffix: 'calculatedSepratorWaterRate',
  }),
  col('calculatedFlowLinePressure', 'pressureAbsolute', 'Calculated flowline pressure.'),
  col('calculatedPressureMatchIndex', 'unitless', 'Calculated pressure match-quality index.'),
];

/**
 * Field-grain well-solver match diagnostics on `NetworkOutputs_Field_Agent`
 * Distinct from the per-string `WELL_SOLVER_MATCH`: this source carries
 * field-rollup measured/estimated/difference TOTAL rates per phase plus
 * per-phase flow match indices — and has NO fbhp/fwht/whp/gor/waterCut solver
 * columns. Bare canonical source columns — `suffix === canonical`, `prefix:''`
 * (pure passthrough); every suffix must resolve.
 */
export const FIELD_WELL_SOLVER_MATCH: Shape = [
  col('wellSolverEstimatedTotalOilRate', 'liquidRate', 'Solver-estimated total oil rate across the field.'),
  col('wellSolverEstimatedTotalGasRate', 'gasRate', 'Solver-estimated total gas rate across the field.'),
  col('wellSolverEstimatedTotalWaterRate', 'liquidRate', 'Solver-estimated total water rate across the field.'),
  col('wellSolverEstimatedTotalLiquidRate', 'liquidRate', 'Solver-estimated total liquid rate across the field.'),
  col('wellSolverMeasuredTotalOilRate', 'liquidRate', 'Solver-measured total oil rate across the field.'),
  col('wellSolverMeasuredTotalGasRate', 'gasRate', 'Solver-measured total gas rate across the field.'),
  col('wellSolverMeasuredTotalWaterRate', 'liquidRate', 'Solver-measured total water rate across the field.'),
  col('wellSolverMeasuredTotalLiquidRate', 'liquidRate', 'Solver-measured total liquid rate across the field.'),
  col('wellSolverOilDifference', 'liquidRate', 'Solver measured-vs-estimated oil difference.'),
  col('wellSolverGasDifference', 'gasRateModel', 'Solver measured-vs-estimated gas difference.'),
  col('wellSolverWaterDifference', 'liquidRate', 'Solver measured-vs-estimated water difference.'),
  col('wellSolverLiquidDifference', 'liquidRate', 'Solver measured-vs-estimated liquid difference.'),
  col('wellSolverOilFlowMatchIndex', 'unitless', 'Solver oil-rate flow match-quality index.'),
  col('wellSolverGasFlowMatchIndex', 'unitless', 'Solver gas-rate flow match-quality index.'),
  col('wellSolverWaterFlowMatchIndex', 'unitless', 'Solver water-rate flow match-quality index.'),
];

/**
 * Field-grain flowline flow-assurance flags on `NetworkOutputs_Field_Agent`
 * Distinct from the per-hazard `FLOW_ASSURANCE_FLAGS`: this source only
 * carries the two estimated-flowline flags. Bare canonical source columns —
 * `suffix === canonical`, `prefix:''` (pure passthrough). Both source columns
 * are irregularly cased (`estimatedFlowlineflowassuranceflag`); the verbatim
 * casing must be preserved to resolve.
 */
export const FIELD_FLOWLINE_FLAGS: Shape = [
  col('estimatedFlowlineUnstableflowFlag', 'unitless', 'Estimated flowline unstable-flow risk flag.'),
  col('estimatedFlowlineFlowAssuranceFlag', 'unitless', 'Estimated flowline flow-assurance risk flag.', {
    suffix: 'estimatedFlowlineflowassuranceflag',
  }),
];
/**
 * The entity-identity scalars carried by every alarm source:
 * `entityName` / `entityExternalId` / `entityType`. The alarm sources model the
 * alarmed entity as THREE scalar columns (not one direct-relation), but they are
 * the entity grain ref the agent scopes by / joins, so they are kept verbatim
 * alongside identity (passthrough, like `IDENTITY` — the source carries no
 * prefix, so `prefix:''` yields `source === canonical` and no rename). The lone
 * direct-relation, `wellString`, is reused from `STRING_RELATION`.
 */
